import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Skeleton,
    ToggleButton,
    ToggleButtonGroup,
    Chip,
    CircularProgress,
    InputBase,
    IconButton,
    Collapse,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { apiPost } from '../api/apiClient';
import { useTraderTagData } from '../hooks/useTraderTagData';
import { useTopTraders } from '../api/useTopTraders';
import type { TopTrader, TraderSort } from '../api/useTopTraders';
import { fetchTraderPnl } from '../api/fetchTraderPnl';
import { Badge } from '../components/shared';
import { TagBadge } from '../components/TagBadge';
import type { EventMarketDetail } from '../api/types';
import type { MarketTrade } from '../api/useMarketTrades';
import { formatVolume } from '../utils/format';
import { cleanAccount, shortAccount, formatPnl } from '../utils/traderFormat';
import { toChecksumId } from '../utils/ids';
import { useHighlightedIds } from '../../../hooks/useHighlightedIds';
import { useValueFlash } from '../../../hooks/useValueFlash';
import {
    VALUE_FLASH_UP_SX,
    VALUE_FLASH_DOWN_SX,
} from '../../../styles/realtimeAnimations';
import { TradesTable } from './TradesTable';

const TOP_ACCOUNTS = 10;
const TRADES_PAGE_SIZE = 20;

interface LiveTraderDelta {
    buyCount: number;
    sellCount: number;
    volume: number;
}

interface TraderInsightsProps {
    market: EventMarketDetail;
    color: string;
    startDate: Date | null;
    endDate: Date | null;
    highlightedAccounts: Set<string>;
    onToggleAccount: (accountId: string) => void;
    loadingAccounts?: Set<string>;
    accountColorMap: Map<string, string>;
    tradesByAccount?: Map<string, MarketTrade[]>;
    liveTraderDeltas?: Map<string, LiveTraderDelta>;
}

const ACCOUNT_COLORS = [
    '#f59e0b',
    '#3b82f6',
    '#ef4444',
    '#10b981',
    '#a855f7',
    '#ec4899',
    '#06b6d4',
    '#84cc16',
    '#f97316',
    '#14b8a6',
    '#e879f9',
    '#fb7185',
    '#22d3ee',
    '#a3e635',
];

export function getAccountColor(index: number): string {
    return ACCOUNT_COLORS[index % ACCOUNT_COLORS.length];
}

function formatTs(d: Date): string {
    return d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
}

// --- Paginated trade sub-table ---
function tradeKey(t: MarketTrade): string {
    return `${t.timestamp.getTime()}_${t.price}_${t.amount}`;
}

function TradeSubTable({
    trades,
    side,
    color,
}: {
    trades: MarketTrade[];
    side: 'buy' | 'sell';
    color: string;
}) {
    const theme = useTheme();
    const [page, setPage] = useState(0);
    const filtered = useMemo(
        () =>
            trades
                .filter(t => t.side === side)
                .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime()),
        [trades, side]
    );
    const highlightedTradeIds = useHighlightedIds(filtered, tradeKey);
    const totalPages = Math.max(
        1,
        Math.ceil(filtered.length / TRADES_PAGE_SIZE)
    );
    const pageItems = filtered.slice(
        page * TRADES_PAGE_SIZE,
        (page + 1) * TRADES_PAGE_SIZE
    );

    if (filtered.length === 0) {
        return (
            <Typography
                sx={{
                    fontSize: '10px',
                    color: 'text.disabled',
                    py: 1,
                    textAlign: 'center',
                }}
            >
                No {side}s
            </Typography>
        );
    }

    return (
        <Box>
            <Box
                component="table"
                sx={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '10px',
                    '& th': {
                        textAlign: 'right',
                        fontWeight: 600,
                        fontSize: '9px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        color: 'text.secondary',
                        py: 0.25,
                        px: 0.4,
                        borderBottom: 1,
                        borderColor: 'divider',
                        '&:first-of-type': { textAlign: 'left' },
                    },
                    '& td': {
                        py: 0.25,
                        px: 0.4,
                        fontFamily: 'monospace',
                        textAlign: 'right',
                        borderBottom: 1,
                        borderColor: alpha(theme.palette.divider, 0.2),
                        '&:first-of-type': { textAlign: 'left' },
                    },
                }}
            >
                <thead>
                    <tr>
                        <th>Time</th>
                        <th>Price</th>
                        <th>Qty</th>
                        <th>Value</th>
                    </tr>
                </thead>
                <tbody>
                    {pageItems.map((t, i) => {
                        const key = tradeKey(t);
                        const isNew = highlightedTradeIds.has(key);
                        return (
                            <tr
                                key={`${key}-${i}`}
                                style={
                                    isNew
                                        ? {
                                              animation:
                                                  'rtHighlightFlash 1.2s ease-out forwards',
                                          }
                                        : undefined
                                }
                            >
                                <td
                                    style={{
                                        fontSize: '9px',
                                        fontFamily: 'monospace',
                                    }}
                                >
                                    {formatTs(t.timestamp)}
                                </td>
                                <td>{(t.price * 100).toFixed(1)}¢</td>
                                <td>{t.amount.toFixed(2)}</td>
                                <td>${t.quoteAmount.toFixed(2)}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </Box>
            {totalPages > 1 && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.5,
                        mt: 0.5,
                    }}
                >
                    <IconButton
                        size="small"
                        disabled={page === 0}
                        onClick={() => setPage(p => p - 1)}
                        sx={{ p: 0.15 }}
                    >
                        <ChevronLeftIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                    <Typography
                        sx={{
                            fontSize: '9px',
                            color: 'text.secondary',
                            fontFamily: 'monospace',
                        }}
                    >
                        {page + 1}/{totalPages} ({filtered.length})
                    </Typography>
                    <IconButton
                        size="small"
                        disabled={page >= totalPages - 1}
                        onClick={() => setPage(p => p + 1)}
                        sx={{ p: 0.15 }}
                    >
                        <ChevronRightIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                </Box>
            )}
        </Box>
    );
}

// Extracted as a component so it can call useTraderTagsCached as a hook.
function TraderRow({
    t,
    active,
    isLoading,
    isExpanded,
    accountTrades,
    hasTrades,
    acctColor,
    tags,
    dataIssues,
    liveDelta,
    onToggle,
    onExpand,
    onNavigate,
}: {
    t: TopTrader;
    active: boolean;
    isLoading: boolean;
    isExpanded: boolean;
    accountTrades: MarketTrade[] | undefined;
    hasTrades: boolean;
    acctColor: string;
    tags: string[];
    dataIssues?: string[];
    liveDelta?: LiveTraderDelta;
    onToggle: () => void;
    onExpand: () => void;
    onNavigate: () => void;
}) {
    const theme = useTheme();
    const totalVol = t.total_volume + (liveDelta?.volume ?? 0);
    const buyCount = t.buy_count + (liveDelta?.buyCount ?? 0);
    const sellCount = t.sell_count + (liveDelta?.sellCount ?? 0);
    const volFlash = useValueFlash(totalVol, liveDelta);
    const buyFlash = useValueFlash(buyCount, liveDelta);
    const sellFlash = useValueFlash(sellCount, liveDelta);

    const flashCountRef = useRef({ vol: 0, buy: 0, sell: 0 });
    const wasFlashingRef = useRef({ vol: false, buy: false, sell: false });
    if (volFlash && !wasFlashingRef.current.vol) flashCountRef.current.vol++;
    wasFlashingRef.current.vol = !!volFlash;
    if (buyFlash && !wasFlashingRef.current.buy) flashCountRef.current.buy++;
    wasFlashingRef.current.buy = !!buyFlash;
    if (sellFlash && !wasFlashingRef.current.sell) flashCountRef.current.sell++;
    wasFlashingRef.current.sell = !!sellFlash;

    const flashSx = (f: 'up' | 'down' | null) =>
        f === 'up'
            ? VALUE_FLASH_UP_SX
            : f === 'down'
              ? VALUE_FLASH_DOWN_SX
              : {};

    return (
        <>
            <tr
                onClick={onToggle}
                style={{
                    background: active ? alpha(acctColor, 0.08) : undefined,
                }}
            >
                <td>
                    {isLoading ? (
                        <CircularProgress size={8} sx={{ color: acctColor }} />
                    ) : (
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: acctColor,
                                opacity: active ? 1 : 0.4,
                                transition: 'opacity 0.15s',
                            }}
                        />
                    )}
                </td>
                <td
                    style={{ fontWeight: active ? 700 : 400, fontSize: '10px' }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            flexWrap: 'wrap',
                        }}
                    >
                        <span>{cleanAccount(t.account_id)}</span>
                        {tags.map(tag => (
                            <TagBadge
                                key={tag}
                                tag={tag}
                                dataIssues={dataIssues}
                            />
                        ))}
                    </Box>
                </td>
                <td
                    style={{
                        color:
                            t.total_pnl >= 0
                                ? theme.palette.success.main
                                : theme.palette.error.main,
                        fontWeight: 600,
                    }}
                >
                    {formatPnl(t.total_pnl)}
                </td>
                <td>
                    <Box
                        key={`vol-${flashCountRef.current.vol}`}
                        component="span"
                        sx={flashSx(volFlash)}
                    >
                        {formatVolume(totalVol)}
                    </Box>
                </td>
                <td>
                    <Box
                        key={`buy-${flashCountRef.current.buy}`}
                        component="span"
                        sx={{ color: 'success.main', ...flashSx(buyFlash) }}
                    >
                        {buyCount}
                    </Box>
                    {'/'}
                    <Box
                        key={`sell-${flashCountRef.current.sell}`}
                        component="span"
                        sx={{ color: 'error.main', ...flashSx(sellFlash) }}
                    >
                        {sellCount}
                    </Box>
                </td>
                <td>{t.avg_buy_price.toFixed(2)}</td>
                <td>{t.sell_count > 0 ? t.avg_sell_price.toFixed(2) : '–'}</td>
                <td style={{ width: 40, textAlign: 'center' }}>
                    <Box
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.25,
                        }}
                    >
                        <Box
                            component="span"
                            onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                onNavigate();
                            }}
                            sx={{
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                opacity: 0.4,
                                '&:hover': { opacity: 1 },
                            }}
                        >
                            <OpenInNewIcon
                                sx={{ fontSize: 12, color: 'text.secondary' }}
                            />
                        </Box>
                        {active && hasTrades && (
                            <Box
                                component="span"
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    onExpand();
                                }}
                                sx={{
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                }}
                            >
                                {isExpanded ? (
                                    <ExpandLessIcon
                                        sx={{
                                            fontSize: 14,
                                            color: 'text.secondary',
                                        }}
                                    />
                                ) : (
                                    <ExpandMoreIcon
                                        sx={{
                                            fontSize: 14,
                                            color: 'text.secondary',
                                        }}
                                    />
                                )}
                            </Box>
                        )}
                    </Box>
                </td>
            </tr>
            {active && hasTrades && isExpanded && (
                <tr key={`${t.account_id}-detail`}>
                    <td colSpan={8} style={{ padding: 0 }}>
                        <Box
                            sx={{
                                borderLeft: `3px solid ${acctColor}`,
                                bgcolor: alpha(acctColor, 0.03),
                                mx: 0.5,
                                mb: 0.5,
                                borderRadius: '0 0 4px 4px',
                            }}
                        >
                            <TradesTable
                                trades={accountTrades!}
                                accentColor={acctColor}
                            />
                        </Box>
                    </td>
                </tr>
            )}
        </>
    );
}

export function TraderInsights({
    market,
    color,
    startDate,
    endDate,
    highlightedAccounts,
    onToggleAccount,
    loadingAccounts,
    accountColorMap,
    tradesByAccount,
    liveTraderDeltas,
}: TraderInsightsProps) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const navigate = useNavigate();
    const [sort, setSort] = useState<TraderSort>('best');
    const [searchValue, setSearchValue] = useState('');
    const [searchFocused, setSearchFocused] = useState(false);
    const [searchedTraders, setSearchedTraders] = useState<
        Map<string, TopTrader>
    >(new Map());
    const [searchLoading, setSearchLoading] = useState<Set<string>>(new Set());
    const [expandedTraders, setExpandedTraders] = useState<Set<string>>(
        new Set()
    );
    const inputRef = useRef<HTMLInputElement>(null);

    const {
        data: traders,
        loading,
        error,
    } = useTopTraders(
        market.condition_id,
        startDate,
        endDate,
        sort,
        TOP_ACCOUNTS
    );

    const allKnownTraders = useMemo(() => {
        const map = new Map<string, TopTrader>();
        for (const t of traders) map.set(t.account_id, t);
        for (const [id, t] of searchedTraders) map.set(id, t);
        return map;
    }, [traders, searchedTraders]);

    // Re-fetch P&L for highlighted accounts that dropped out of top-N after zoom
    const traderIds = useMemo(
        () => new Set(traders.map(t => t.account_id)),
        [traders]
    );
    useEffect(() => {
        const missing = [...highlightedAccounts].filter(
            id => !traderIds.has(id)
        );
        if (missing.length === 0) return;
        for (const accountId of missing) {
            fetchTraderPnl(
                market.condition_id,
                accountId,
                startDate?.toISOString(),
                endDate?.toISOString()
            )
                .then(pnl => {
                    if (pnl) {
                        setSearchedTraders(prev => {
                            const next = new Map(prev);
                            next.set(accountId, pnl);
                            return next;
                        });
                    }
                })
                .catch(() => {});
        }
    }, [
        highlightedAccounts,
        traderIds,
        market.condition_id,
        startDate,
        endDate,
    ]);

    const handleSearch = useCallback(async () => {
        const accountId = toChecksumId(searchValue.trim());
        if (!accountId) return;

        // Canonicalize the typed/pasted address the same way the selection set is
        // keyed, so a wrong-case or bare paste matches an already-selected trader
        // instead of being tracked as a second entry.
        setSearchValue('');

        if (allKnownTraders.has(accountId)) {
            if (!highlightedAccounts.has(accountId)) {
                onToggleAccount(accountId);
            }
            return;
        }

        setSearchLoading(prev => new Set([...prev, accountId]));

        try {
            const pnl = await fetchTraderPnl(
                market.condition_id,
                accountId,
                startDate?.toISOString(),
                endDate?.toISOString()
            );
            if (pnl) {
                setSearchedTraders(prev => {
                    const next = new Map(prev);
                    next.set(accountId, pnl);
                    return next;
                });
                if (!highlightedAccounts.has(accountId)) {
                    onToggleAccount(accountId);
                }
            }
        } catch (err) {
            console.error('Failed to fetch trader PnL:', err);
        } finally {
            setSearchLoading(prev => {
                const next = new Set(prev);
                next.delete(accountId);
                return next;
            });
        }
    }, [
        searchValue,
        market.condition_id,
        startDate,
        endDate,
        allKnownTraders,
        highlightedAccounts,
        onToggleAccount,
    ]);

    const handleRemoveSearched = useCallback(
        (accountId: string) => {
            if (highlightedAccounts.has(accountId)) {
                onToggleAccount(accountId);
            }
            setSearchedTraders(prev => {
                const next = new Map(prev);
                next.delete(accountId);
                return next;
            });
        },
        [highlightedAccounts, onToggleAccount]
    );

    const toggleExpanded = useCallback((accountId: string) => {
        setExpandedTraders(prev => {
            const next = new Set(prev);
            if (next.has(accountId)) next.delete(accountId);
            else next.add(accountId);
            return next;
        });
    }, []);

    const selectedWithPnl = useMemo(() => {
        const result: Array<{
            accountId: string;
            trader: TopTrader | null;
            color: string;
        }> = [];
        for (const id of highlightedAccounts) {
            result.push({
                accountId: id,
                trader: allKnownTraders.get(id) ?? null,
                color: accountColorMap.get(id) ?? '#888',
            });
        }
        return result;
    }, [highlightedAccounts, allKnownTraders, accountColorMap]);

    const allTraderIds = useMemo(
        () => [
            ...new Set([
                ...traders.map(t => t.account_id),
                ...highlightedAccounts,
            ]),
        ],
        [traders, highlightedAccounts]
    );
    const { tags: tagsMap, dataIssues: issuesMap } = useTraderTagData(
        allTraderIds,
        cleanAccount
    );

    if (loading) {
        return (
            <Box>
                <Skeleton
                    variant="rectangular"
                    height={32}
                    sx={{ borderRadius: 1, mb: 1 }}
                />
                <Skeleton
                    variant="rectangular"
                    height={120}
                    sx={{ borderRadius: 1 }}
                />
            </Box>
        );
    }

    if (error || traders.length === 0) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '12px', color: 'text.disabled' }}>
                    {error ? 'Failed to load traders' : 'No traders found'}
                </Typography>
            </Box>
        );
    }

    const renderTraderRowEl = (t: TopTrader) => {
        const acctColor = accountColorMap.get(t.account_id) ?? '#888';
        const active = highlightedAccounts.has(t.account_id);
        const isLoading = loadingAccounts?.has(t.account_id) ?? false;
        const isExpanded = expandedTraders.has(t.account_id);
        const accountTrades = tradesByAccount?.get(t.account_id);
        const hasTrades = !!(accountTrades && accountTrades.length > 0);
        const delta = liveTraderDeltas?.get(t.account_id);

        return (
            <TraderRow
                key={t.account_id}
                t={t}
                active={active}
                isLoading={isLoading}
                isExpanded={isExpanded}
                accountTrades={accountTrades}
                hasTrades={hasTrades}
                acctColor={acctColor}
                tags={tagsMap.get(cleanAccount(t.account_id)) ?? []}
                dataIssues={issuesMap.get(cleanAccount(t.account_id))}
                liveDelta={delta}
                onToggle={() => onToggleAccount(t.account_id)}
                onExpand={() => toggleExpanded(t.account_id)}
                onNavigate={() =>
                    navigate(`/markets/trader/${cleanAccount(t.account_id)}`)
                }
            />
        );
    };

    return (
        <Box>
            {/* Header row: title + search + toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography
                    sx={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: 'text.secondary',
                        whiteSpace: 'nowrap',
                    }}
                >
                    Traders
                </Typography>

                {/* Search — matching AssetSearchOverlay style */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 0.75,
                        py: 0.25,
                        bgcolor: isDark
                            ? alpha('#fff', 0.06)
                            : alpha('#000', 0.04),
                        border: 1,
                        borderColor: searchFocused
                            ? 'primary.main'
                            : 'transparent',
                        borderRadius: '4px',
                        transition: 'border-color 0.15s',
                        flex: 1,
                        minWidth: 0,
                    }}
                >
                    {searchLoading.size > 0 ? (
                        <CircularProgress
                            size={12}
                            sx={{ color: 'text.disabled', flexShrink: 0 }}
                        />
                    ) : (
                        <SearchIcon
                            sx={{
                                fontSize: 13,
                                color: 'text.disabled',
                                flexShrink: 0,
                            }}
                        />
                    )}
                    <InputBase
                        inputRef={inputRef}
                        value={searchValue}
                        onChange={e => setSearchValue(e.target.value)}
                        onFocus={() => setSearchFocused(true)}
                        onBlur={() => setSearchFocused(false)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') handleSearch();
                        }}
                        placeholder="Add account…"
                        sx={{
                            flex: 1,
                            fontSize: '11px',
                            fontFamily: 'monospace',
                            '& input': { py: 0, height: 20 },
                            '& input::placeholder': {
                                color: 'text.disabled',
                                opacity: 0.7,
                            },
                        }}
                    />
                    {searchValue && (
                        <Box
                            component="span"
                            onClick={() => setSearchValue('')}
                            sx={{
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            <CloseIcon
                                sx={{ fontSize: 12, color: 'text.disabled' }}
                            />
                        </Box>
                    )}
                </Box>

                <ToggleButtonGroup
                    value={sort}
                    exclusive
                    onChange={(_, v) => {
                        if (v) setSort(v);
                    }}
                    size="small"
                    sx={{
                        height: 24,
                        flexShrink: 0,
                        '& .MuiToggleButton-root': {
                            fontSize: '10px',
                            px: 1,
                            py: 0,
                            textTransform: 'none',
                        },
                    }}
                >
                    <ToggleButton value="best">Best</ToggleButton>
                    <ToggleButton value="worst">Worst</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* Selected account chips with P&L */}
            {selectedWithPnl.length > 0 && (
                <Box
                    sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}
                >
                    {selectedWithPnl.map(
                        ({ accountId, trader, color: acctColor }) => {
                            const isTradeLoading =
                                loadingAccounts?.has(accountId);
                            const pnlText = trader
                                ? ` ${formatPnl(trader.total_pnl)}`
                                : '';
                            const pnlColor = trader
                                ? trader.total_pnl >= 0
                                    ? theme.palette.success.main
                                    : theme.palette.error.main
                                : undefined;
                            const isSearched = searchedTraders.has(accountId);

                            return (
                                <Chip
                                    key={accountId}
                                    size="small"
                                    label={
                                        <Box
                                            component="span"
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                            }}
                                        >
                                            {isTradeLoading && (
                                                <CircularProgress
                                                    size={8}
                                                    sx={{ color: acctColor }}
                                                />
                                            )}
                                            <span>
                                                {shortAccount(accountId)}
                                            </span>
                                            {pnlText && (
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: pnlColor,
                                                    }}
                                                >
                                                    {pnlText}
                                                </Box>
                                            )}
                                        </Box>
                                    }
                                    onClick={() => onToggleAccount(accountId)}
                                    onDelete={
                                        isSearched
                                            ? () =>
                                                  handleRemoveSearched(
                                                      accountId
                                                  )
                                            : undefined
                                    }
                                    deleteIcon={
                                        isSearched ? (
                                            <CloseIcon
                                                sx={{
                                                    fontSize: '12px !important',
                                                }}
                                            />
                                        ) : undefined
                                    }
                                    sx={{
                                        fontSize: '10px',
                                        fontFamily: 'monospace',
                                        height: 22,
                                        borderLeft: `3px solid ${acctColor}`,
                                        bgcolor: alpha(acctColor, 0.12),
                                        fontWeight: 600,
                                        '& .MuiChip-deleteIcon': {
                                            color: 'text.secondary',
                                            '&:hover': {
                                                color: 'text.primary',
                                            },
                                        },
                                    }}
                                />
                            );
                        }
                    )}
                </Box>
            )}

            {/* Trader table */}
            <Box
                component="table"
                sx={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '11px',
                    '& th': {
                        textAlign: 'right',
                        fontWeight: 600,
                        fontSize: '9.5px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        color: 'text.secondary',
                        py: 0.4,
                        px: 0.5,
                        borderBottom: 1,
                        borderColor: 'divider',
                        '&:first-of-type, &:nth-of-type(2)': {
                            textAlign: 'left',
                        },
                    },
                    '& td': {
                        py: 0.4,
                        px: 0.5,
                        borderBottom: 1,
                        borderColor: alpha(theme.palette.divider, 0.3),
                        fontFamily: 'monospace',
                        textAlign: 'right',
                        '&:first-of-type, &:nth-of-type(2)': {
                            textAlign: 'left',
                        },
                    },
                    '& tbody tr': {
                        cursor: 'pointer',
                        transition: 'background 0.1s',
                        '&:hover td': { bgcolor: 'action.hover' },
                    },
                }}
            >
                <thead>
                    <tr>
                        <th style={{ width: 12 }}></th>
                        <th>Account</th>
                        <th>P&L</th>
                        <th>Vol</th>
                        <th>B/S</th>
                        <th>Avg B</th>
                        <th>Avg S</th>
                        <th style={{ width: 40 }}></th>
                    </tr>
                </thead>
                <tbody>
                    {traders.map(t => renderTraderRowEl(t))}
                    {/* Pinned: highlighted accounts not in current top traders list */}
                    {[...highlightedAccounts]
                        .filter(id => !traders.some(t => t.account_id === id))
                        .map(id => {
                            const t = allKnownTraders.get(id);
                            return t ? renderTraderRowEl(t) : null;
                        })}
                </tbody>
            </Box>
        </Box>
    );
}
